// 2026-09-07 신규 — 등급제 배포 안내(첫 로그인 전면 안내 C안 · 홈 상단 띠 A) 의 순수 로직.
// 기획: docs/ops/tier-rollout-communication-plan.md §3(규격) · §5(세그먼트별 문안). 시안: docs/design/tier-rollout-communication-variants.html
//
// 원칙
//  * 문장은 이 사전에서만 고른다(개인화 서술을 새로 만들지 않는다 — MyTier.md 🔒 와 같은 원칙).
//  * 순위·컷 수치·산식은 쓰지 않는다(internal-policy.md §7). 자극은 크기와 대비(컴포넌트)로, 문장은 차분하게.
//  * 「그대로」「10월 1일부터」「결제는 11월 1일부터」「지금은 0원」을 반복한다 — 잃는 게 없다 · 갑자기가 아니다 · 지금 금액이 확정이 아니다.
//  * 가입비는 기존 회원에게 「해지 후 재시작 때만」이라고 붙인다 — "나도 30만원 내야 하나" 오해 방지.
//  * 대표 확정(2026-09-22): 등급제(등급 결정)는 시작일(system_from, 10/1)부터. 기존 회원(9/23 까지 승인)은 10/31 까지 **마스터 등급 혜택**
//    (채널 한도 없음 · 루키 작품 규칙 미적용) 그대로 + 멤버십 구독료 없음, 11/1(effective_from)부터 내 등급 기준 멤버십 구독료·채널 한도.
//    신규 회원(cutoff 이후 승인)은 승인 즉시. 「아무것도 바뀌지 않아요」 문장은 쓰지 않는다 — 「마스터 등급 혜택 그대로」로 말한다.
import { TIER_MONTHLY_FEE, SIGNUP_FEE, REACTIVATION_FEE, formatKrw, billingDateLabel, TIER_SYSTEM_START } from "./tierPricing";
import { guideUrl } from "./guideLinks";
import { tt, ttl } from "../i18n/tt";
import { lazyI18n } from "../i18n/lazyText";
import { fmtMonth, fmtMonthDay } from "./formatLocale";

// 문의처(대표 결정 2026-09-07) — 별도 창구 대신 **이용 가이드의 요금표 화면**과 그 화면의 **가이드 도우미 레이**로 안내한다.
export const ROLLOUT_HELP = lazyI18n(() => Object.freeze({
  text: tt("a.9cf82301", "궁금한 점은 이용 가이드의 요금표에서 확인하거나, 가이드 도우미 레이에게 물어보세요."),
  label: tt("a.3f5b994b", "요금표 보기 · 레이에게 묻기"),
  url: guideUrl("c-tier-overview"),
}));

export const ROLLOUT_QUERY_PARAM = "rollout"; // ?rollout=gold — 조건과 무관하게 강제(QA·디자인 검토). 확인 기록은 하지 않는다.
export const ROLLOUT_STRIP_ID = "tier-rollout"; // 홈 상단 띠 dismiss 키(guideFlow.dismissBanner, 30일)

const DAY_MS = 24 * 60 * 60 * 1000;
const FEE_TIERS = ["실버", "골드", "플래티넘"];
const EXEMPT_TIERS = ["다이아몬드", "마스터"];

/** RPC get_tier_rollout_notice_state(jsonb) → 화면용 객체. 구버전 서버(키 없음)는 기본값으로 — 안내는 뜨지 않는다. */
export function normalizeRolloutNoticeState(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    acked: Boolean(raw.acked),
    ackedAt: raw.acked_at ?? null,
    legacy: raw.legacy === undefined ? true : Boolean(raw.legacy),
    cutoff: raw.cutoff ?? null,
    effectiveFrom: raw.effective_from ?? null, // 기존 회원 첫 결제일(멤버십 구독료만)
    systemFrom: raw.system_from ?? TIER_SYSTEM_START, // 등급제 시작일(채널 한도·루키 규칙, 전원) — 구버전 서버는 기본값
    grace: Boolean(raw.grace),
    tier: raw.tier ?? null,
    status: raw.status ?? null, // rookie · pre-rookie · normal(정식) · dormant · none
    monthlyFee: Number(raw.monthly_fee ?? 0) || 0,
    exempt: Boolean(raw.exempt),
    channelCount: Number(raw.channel_count ?? 0) || 0,
    channelLimit: raw.channel_limit === undefined || raw.channel_limit === null ? null : Number(raw.channel_limit),
    isRightsHolder: Boolean(raw.is_rights_holder),
    role: raw.role ?? null,
  };
}

/**
 * 세그먼트 판정 — §5 표의 행 하나.
 *  new      신규 회원(cutoff 이후 승인) — 지금부터 적용
 *  paid     실버·골드·플래티넘(기존 회원)
 *  exempt   다이아몬드·마스터
 *  rookie   루키(정식 등급 전, 채널 있음)
 *  pre      채널은 있으나 등급 행 없음(pre-rookie)
 *  dormant  휴면
 *  none     채널 0개 일반 회원
 */
export function rolloutSegment(state) {
  if (!state) return null;
  if (!state.legacy) return "new";
  if (state.status === "dormant") return "dormant";
  if (state.tier && EXEMPT_TIERS.includes(state.tier)) return "exempt";
  if (state.tier && FEE_TIERS.includes(state.tier)) return "paid";
  if (state.status === "rookie") return "rookie";
  if (state.status === "pre-rookie" || (state.channelCount > 0 && !state.tier)) return "pre";
  if (state.status === "normal" && state.tier) return "paid";
  return "none";
}

/** 전면 안내를 띄우나 — 대상(크리에이터) · 미확인 · 상태 조회 성공. 강제 파라미터는 컴포넌트에서 따로 본다. */
export function shouldShowInterstitial(state, { isTarget = true } = {}) {
  if (!state || !isTarget) return false;
  if (state.isRightsHolder) return false;
  if (state.acked) return false;
  return true;
}

/** 주어진 날(등급제 시작일 등)까지 남은 날(첫 노출 시점 기준으로 고정해 쓴다 — 컴포넌트가 최초 값을 state 에 보관). 지났으면 0. */
export function daysUntil(iso, now = new Date()) {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const target = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00+09:00`).getTime();
  const d = Math.ceil((target - now.getTime()) / DAY_MS);
  return d > 0 ? d : 0;
}

const fee = (tier) => formatKrw(TIER_MONTHLY_FEE[tier] ?? 0);
// 2026-09-07 다국어 — 문장은 전부 사전(`tt`)에서. 한국어는 종전과 바이트 동일(테스트가 지킨다). 모듈 상수가 아니라 함수로 두어 언어가 정해진 뒤에 만든다.
const feeTable = () => tt("a.8c789dba", "실버 월 {{v0}} · 골드 {{v1}} · 플래티넘 {{v2}}", { v0: fee("실버"), v1: fee("골드"), v2: fee("플래티넘") });
const signupLine = () => ({
  k: tt("a.32f55d36", "가입비 · 재활성화비"),
  v: tt("a.496cbd8f", "신규 {{v0}} · 재시작 {{v1}}", { v0: formatKrw(SIGNUP_FEE), v1: formatKrw(REACTIVATION_FEE) }),
  s: tt("a.cde4aba2", "기존 회원은 해당 없어요. 해지한 뒤 다시 시작할 때만 재활성화비가 있어요."),
});

/**
 * 전면 안내 문안 — 머리(공통) + 카드 3장(세그먼트별). 날짜는 서버 값(system_from · effective_from)에서 만든다.
 *  - 큰 글자 = 등급제 시작일(system_from, 10/1) · 둘째 줄 = 「10/31 까지 마스터 등급 혜택 그대로 · 결제는 11/1 부터」(기존 회원)
 * @returns {{ kicker, dateBig, dateSuffix, headline, calm:{lead,rest}, cards:[{k,v,em?,s}], fine, ctaPrimary, ctaSecondary }}
 */
export function buildInterstitialCopy(state, { now = new Date() } = {}) {
  const seg = rolloutSegment(state);
  if (!seg) return null;
  const from = billingDateLabel(state.effectiveFrom) || fmtMonthDay(11, 1);
  const fromMonth = billingDateLabel(state.effectiveFrom, { monthOnly: true }) || fmtMonth(11);
  const beforeLabel = dayBeforeLabel(state.effectiveFrom) || fmtMonthDay(10, 31);
  const systemFrom = state.systemFrom || TIER_SYSTEM_START;
  const start = billingDateLabel(systemFrom) || fmtMonthDay(10, 1);
  // 기존 회원 공통 둘째 줄(2026-09-22 확정) — 10/31 까지 마스터 등급 혜택 그대로(채널 한도 없음) · 결제는 11/1 부터
  const graceLead = tt("a.tier.graceLead", "기존 회원은 {{v0}}까지 마스터 등급 혜택 그대로예요.", { v0: beforeLabel });
  const graceRest = tt("a.tier.graceRest", "멤버십 구독료 없음 · 채널 한도 없음 · 결제는 {{v0}}부터예요.", { v0: from });
  const channelGrace = tt("a.tier.channelGrace", "{{v0}}까지 한도 없음 · {{v1}}부터 등급별 채널 한도", { v0: beforeLabel, v1: from });
  const tier = state.tier;
  const myFee = fee(tier);
  const channelWord = state.channelCount > 0 ? tt("a.7ea95b0e", "지금 운영 중인 채널 {{v0}}개는 그대로예요.", { v0: state.channelCount }) : tt("a.25617e4a", "지금 채널은 그대로예요.");
  const tierText = ttl(tier);

  const head = {
    kicker: tt("a.e5527b78", "레이블리 등급제 안내"),
    dateBig: start,
    // 영어는 「From Oct 1」(앞), 한국어·일본어·중국어는 「11월 1일부터」(뒤) — 접두어·접미어 둘 다 사전에서(2026-09-07 대표 지적 「onward」)
    datePrefix: tt("a.rollout.datePrefix", ""),
    dateSuffix: tt("a.132922b9", "부터"),
    headline: tt("a.f35e965d", "등급제가 시작됩니다"),
    calm: { lead: graceLead, rest: graceRest },
    fine: tt("a.5721df98", "「자세히 보기」에서 요금표·채널 한도·일정을 볼 수 있어요. 이 안내는 한 번만 보이고, 설정 › 「등급제 안내」에서 언제든 다시 볼 수 있어요."),
    ctaPrimary: tt("a.c9e5437d", "확인했어요"),
    ctaSecondary: tt("a.6fef9a4f", "자세히 보기"),
    help: ROLLOUT_HELP,
    days: daysUntil(systemFrom, now),
    segment: seg,
  };

  switch (seg) {
    case "paid":
      return {
        ...head,
        calm: { lead: graceLead, rest: graceRest },
        cards: [
          { k: tt("a.6ad14ff3", "내 등급 · {{v0}}", { v0: tierText }), v: tt("a.98370fdb", "{{v0}}부터 ", { v0: fromMonth }), em: tt("a.a8ea3b9a", "월 {{v0}}", { v0: myFee }), tail: tt("a.22418b17", " · 지금은 0원"), s: tt("a.74f16138", "청구 금액은 그달 1일에 결정된 등급으로 정해져요. 등급은 활동을 종합 평가해 매월 1일 결정돼요.") },
          { k: tt("a.f0c83980", "채널"), v: channelGrace, s: tt("a.ea946ab7", "{{v0}} 한도를 넘는 새 채널 추가만 등급이 오르면 가능해요.", { v0: channelWord }) },
          signupLine(),
        ],
      };
    case "exempt":
      return {
        ...head,
        calm: { lead: tt("a.25c247db", "{{v0}} 이상은 달라지는 게 없어요.", { v0: tier }), rest: "" },
        cards: [
          { k: tt("a.6ad14ff3", "내 등급 · {{v0}}", { v0: tierText }), v: tt("a.4c896129", "멤버십 구독료 "), em: tt("a.acd64428", "면제"), s: tt("a.fe8b1e70", "채널 한도도 없어요.") },
          { k: tt("a.f0c83980", "채널"), v: tt("a.08184d5a", "한도 없음"), s: channelWord },
          { k: tt("a.3b479fa8", "새로 생긴 것"), v: tt("a.6541162f", "등급 가점 · 클린 마크"), s: tt("a.af88fa7e", "홈에서 확인해 보세요.") },
        ],
      };
    case "rookie":
      return {
        ...head,
        calm: { lead: tt("a.1f158ccf", "루키는 멤버십 구독료가 없어요."), rest: tt("a.tier.rookieRest", "{{v0}}까지 마스터 등급 혜택 · 루키 작품 규칙은 {{v1}}부터예요. 지금까지 올린 영상은 그대로 인정돼요.", { v0: beforeLabel, v1: from }) },
        cards: [
          { k: tt("a.94159137", "내 등급 · 루키"), v: tt("a.92ea2e5f", "멤버십 구독료 없음 · 5편 채우고 승인되면 실버"), s: tt("a.3a770ac2", "실버는 월 {{v0}}. 기존 회원은 {{v1}}부터 청구돼요.", { v0: fee("실버"), v1: fromMonth }) },
          { k: tt("a.ef11cd27", "5편 세는 법"), v: tt("a.777b0502", "이제 "), em: tt("a.ed9697eb", "루키 작품"), tail: tt("a.f3fe9af7", "으로 세요"), s: tt("a.d0bbd061", "지금까지 올린 영상은 그대로 인정돼요. 홈 「루키 작품만」 필터를 끄면 다른 작품도 보여요.") },
          { k: tt("a.f0c83980", "채널"), v: channelGrace, s: channelWord },
        ],
      };
    case "pre":
      return {
        ...head,
        calm: { lead: graceLead, rest: "" },
        cards: [
          { k: tt("a.82e5f386", "시작"), v: tt("a.14f385e9", "채널을 쓰기 시작하면 루키부터"), s: tt("a.45621c49", "루키는 멤버십 구독료 없음. 루키 작품 영상 5편을 채우고 승인되면 실버예요.") },
          { k: tt("a.f0c83980", "채널"), v: channelGrace, s: tt("a.82e99822", "지금 등록된 채널 {{v0}}개는 모두 그대로예요.", { v0: state.channelCount }) },
          signupLine(),
        ],
      };
    case "dormant":
      return {
        ...head,
        calm: { lead: graceLead, rest: "" },
        cards: [
          // 2026-10-13 대표 결정 — 기존 회원은 청구 시작일 전날까지 돌아오면 재활성화비 면제(시안 C 「복귀 유도형」)
          { k: tt("a.a7c2d5a3", "복귀"), v: tt("a.dw.notice", "기존 회원 혜택 · {{v0}}까지 무료 복귀", { v0: beforeLabel }), s: tt("a.dw.noticeSub", "{{v0}}부터는 돌아올 때 재활성화비 {{v1}}이 1회 있어요. 그 전에 돌아오면 내지 않아요.", { v0: from, v1: formatKrw(REACTIVATION_FEE) }) },
          { k: tt("a.8a97d7be", "멤버십 구독료 · 채널"), v: tt("a.tier.dormantFrom", "멤버십 구독료·채널 한도는 {{v0}}부터 · 그전까지 마스터 등급 혜택", { v0: from }), s: channelWord },
          { k: tt("a.9017b230", "요금표"), v: feeTable(), s: tt("a.41c4ac86", "다이아몬드 이상은 면제예요.") },
        ],
      };
    case "new":
      return {
        ...head,
        dateBig: tt("a.50596b41", "지금"),
        datePrefix: "",
        dateSuffix: tt("a.rollout.nowSuffix", "부터"),
        headline: tt("a.309c991d", "등급제가 적용됩니다"),
        calm: { lead: tt("a.3b747db5", "루키로 시작해요."), rest: tt("a.532ea647", "루키는 월 멤버십 구독료가 없어요.") },
        cards: [
          { k: tt("a.fe8ec32e", "가입비"), v: "", em: formatKrw(SIGNUP_FEE), tail: tt("a.619963d6", " · 결제일부터 한 달 이용 포함"), s: tt("a.b7ac294b", "승인 시 1회. 결제일부터 한 달은 가입비에 포함되고, 그달 남은 날은 날짜만큼만, 다음 달 1일부터 월 구독이에요.") },
          { k: tt("a.f0c83980", "채널"), v: tt("a.bd3dac02", "루키는 채널 1개"), s: tt("a.a287ef6e", "등급이 오르면 한도가 바로 늘어나요.") },
          { k: tt("a.81af544e", "승급"), v: tt("a.058be39f", "루키 작품 영상 5편 → 심사 → 승인 즉시 실버"), s: feeTable() },
        ],
      };
    case "none":
    default:
      return {
        ...head,
        calm: { lead: graceLead, rest: "" },
        cards: [
          { k: tt("a.82e5f386", "시작"), v: tt("a.61fe0e3f", "채널을 등록하면 루키부터"), s: tt("a.d598652a", "루키는 멤버십 구독료 없음.") },
          { k: tt("a.f0c83980", "채널"), v: channelGrace, s: "" },
          signupLine(),
        ],
      };
  }
}

/**
 * 홈 상단 띠(A) 한 줄 — 전면 안내를 닫은 뒤. 세그먼트별 한 문장 + CTA.
 * 등급제 시작일(system_from) 전에는 「10월 1일부터 등급제 시작 · 10월 31일까지는 마스터 등급 혜택 · 결제는 11월 1일부터」, 시작일이 지나면
 * (결제일 전까지 띠가 남는 동안) 「등급제가 적용 중이에요 · 10월 31일까지 마스터 등급 혜택 · 결제는 11월 1일부터」 — 지난 날짜를 「~부터」로 말하지 않는다(2026-09-22).
 */
export function buildStripCopy(state, { now = new Date() } = {}) {
  const seg = rolloutSegment(state);
  if (!seg) return null;
  const from = billingDateLabel(state.effectiveFrom) || fmtMonthDay(11, 1);
  const before = dayBeforeLabel(state.effectiveFrom) || fmtMonthDay(10, 31);
  const systemFrom = state.systemFrom || TIER_SYSTEM_START;
  const start = billingDateLabel(systemFrom) || fmtMonthDay(10, 1);
  const live = daysUntil(systemFrom, now) === 0;
  const cta = { label: tt("a.2116d4e8", "자세히"), to: "/tier-guide" }; // 등급제 안내 페이지(B안, 2026-09-07)
  switch (seg) {
    case "new":
      return { id: ROLLOUT_STRIP_ID, text: tt("a.b2dfa022", "등급제가 적용 중이에요 — 루키는 채널 1개 · 멤버십 구독료 없음"), strong: tt("a.691b1050", "등급제가 적용 중이에요"), cta };
    case "exempt":
      return live
        ? { id: ROLLOUT_STRIP_ID, text: tt("a.tier.stripLiveExempt", "등급제가 적용 중이에요 · {{v0}} 이상은 달라지는 게 없어요", { v0: state.tier }), strong: tt("a.tier.stripLiveStrong", "등급제가 적용 중이에요"), cta }
        : { id: ROLLOUT_STRIP_ID, text: tt("a.a62e71e7", "{{v0}}부터 등급제 시작 · {{v1}} 이상은 달라지는 게 없어요", { v0: start, v1: state.tier }), strong: tt("a.b9e3bb48", "{{v0}}부터", { v0: start }), cta };
    case "rookie":
      return { id: ROLLOUT_STRIP_ID, text: tt("a.86633044", "루키 5편은 이제 루키 작품으로 세요 · 지금까지 올린 영상은 그대로 인정돼요"), strong: tt("a.ed9697eb", "루키 작품"), cta };
    default:
      return live
        ? { id: ROLLOUT_STRIP_ID, text: tt("a.tier.stripLive", "등급제가 적용 중이에요 · {{v0}}까지 마스터 등급 혜택 · 결제는 {{v1}}부터예요", { v0: before, v1: from }), strong: tt("a.tier.stripLiveStrong", "등급제가 적용 중이에요"), cta }
        : { id: ROLLOUT_STRIP_ID, text: tt("a.tier.stripStart", "{{v0}}부터 등급제 시작 · {{v1}}까지는 마스터 등급 혜택 · 결제는 {{v2}}부터예요", { v0: start, v1: before, v2: from }), strong: tt("a.b9e3bb48", "{{v0}}부터", { v0: start }), cta };
  }
}

// "2026-11-01" → "10월 31일" (시행일 전날). 서버 date 문자열만 받는다.
export function dayBeforeLabel(iso) {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() - 1);
  return fmtMonthDay(d.getUTCMonth() + 1, d.getUTCDate());
}

/** ?rollout=paid|exempt|rookie|pre|dormant|new|none — QA 강제 미리보기용 가짜 상태 */
export function readForcedRollout(search) {
  try {
    const v = new URLSearchParams(search || "").get(ROLLOUT_QUERY_PARAM);
    return v && FORCED_STATES[v] ? v : null;
  } catch {
    return null;
  }
}
const FORCED_BASE = { acked: false, legacy: true, cutoff: null, effectiveFrom: "2026-11-01", systemFrom: "2026-10-01", grace: true, exempt: false, channelCount: 1, channelLimit: null, isRightsHolder: false, role: "Creator", ackedAt: null, monthlyFee: 0 };
export const FORCED_STATES = Object.freeze({
  paid: { ...FORCED_BASE, tier: "골드", status: "normal", monthlyFee: 99000, channelCount: 3 },
  exempt: { ...FORCED_BASE, tier: "다이아몬드", status: "normal", exempt: true, channelCount: 6 },
  rookie: { ...FORCED_BASE, tier: null, status: "rookie", channelCount: 1 },
  pre: { ...FORCED_BASE, tier: null, status: "pre-rookie", channelCount: 2 },
  dormant: { ...FORCED_BASE, tier: "실버", status: "dormant", channelCount: 1 },
  new: { ...FORCED_BASE, legacy: false, cutoff: "2026-09-24", grace: false, tier: null, status: "rookie", channelCount: 0 },
  none: { ...FORCED_BASE, tier: null, status: null, channelCount: 0 },
});
